import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// Professional Loading Component for Academic Context
const AcademicContextLoading = () => {
  return (
    <div className="w-full rounded-2xl border border-blue-100 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-blue-100 bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-white/15 animate-pulse" />
          <div className="space-y-2 flex-1">
            <div className="h-4 w-40 rounded bg-white/25 animate-pulse" />
            <div className="h-3 w-56 rounded bg-white/15 animate-pulse" />
          </div>
        </div>
      </div>
      <div className="p-4 md:p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-24 rounded bg-slate-200 animate-pulse" />
              <div className="h-11 rounded-xl bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="h-3 w-32 rounded bg-slate-200 animate-pulse mb-3" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-3 w-16 rounded bg-slate-200 animate-pulse" />
                <div className="h-4 w-20 rounded bg-slate-200 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AcademicContextSelector = ({
  tenantId,
  onContextChange,
  setProgramTypes: setProgramTypesProp,
  setPrograms: setProgramsProp,
  setAcademicYears: setAcademicYearsProp,
  setSemesters: setSemestersProp,
  setTerms: setTermsProp,
  initialContext,
  initialProgramTypes,
  initialPrograms,
  initialAcademicYears,
  initialSemesters,
  initialTerms
}) => {
  // State for dropdown options
  const [programTypes, setProgramTypes] = useState(initialProgramTypes || []);
  const [programs, setPrograms] = useState(initialPrograms || []);
  const [academicYears, setAcademicYears] = useState(initialAcademicYears || []);
  const [semesters, setSemesters] = useState(initialSemesters || []);
  const [terms, setTerms] = useState(initialTerms || []);
  
  // State for selections
  const [programTypeId, setProgramTypeId] = useState(initialContext?.programTypeId || '');
  const [programId, setProgramId] = useState(initialContext?.programId || '');
  const [academicYearId, setAcademicYearId] = useState(initialContext?.academicYearId || '');
  const [semesterId, setSemesterId] = useState(initialContext?.semesterId || '');
  const [termId, setTermId] = useState(initialContext?.termId || '');
  
  // UI state
  const [isSemesterBased, setIsSemesterBased] = useState(initialContext?.isSemesterBased ?? null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  
  // Loading states for different sections
  const [loadingProgramTypes, setLoadingProgramTypes] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
  const [loadingSemestersTerms, setLoadingSemestersTerms] = useState(false);

  // Helper to check if academic year is completed
  const isAcademicYearCompleted = useCallback((academicYear) => {
    return academicYear?.status === 'completed' || 
           academicYear?.isCompleted === true ||
           academicYear?.academicYearStatus === 'completed';
  }, []);

  // Helper to check if semester is completed
  const isSemesterCompleted = useCallback((semester) => {
    return semester?.status === 'completed' || 
           semester?.isCompleted === true ||
           semester?.semesterStatus === 'completed';
  }, []);

  // Helper to check if term is completed
  const isTermCompleted = useCallback((term) => {
    return term?.status === 'completed' || 
           term?.isCompleted === true ||
           term?.termStatus === 'completed';
  }, []);

  // Fetch program types
  useEffect(() => {
    if (!tenantId) return;
    setLoadingProgramTypes(true);
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => {
        const types = res.data.programTypes || [];
        setProgramTypes(types);
        setProgramTypesProp?.(types);
      })
      .catch(() => setError('Failed to load program types'))
      .finally(() => setLoadingProgramTypes(false));
  }, [tenantId, setProgramTypesProp]);

  // Fetch programs when program type changes
  useEffect(() => {
    if (!tenantId || !programTypeId) {
      setPrograms([]);
      return;
    }
    setLoadingPrograms(true);
    api.get('/api/programs', { params: { tenantId, programTypeId, status: 'active' } })
      .then(res => {
        const progs = res.data.programs || [];
        setPrograms(progs);
        setProgramsProp?.(progs);
      })
      .catch(() => setError('Failed to load programs'))
      .finally(() => setLoadingPrograms(false));
  }, [tenantId, programTypeId, setProgramsProp]);

  // Fetch academic years (active and completed)
  useEffect(() => {
    if (!tenantId) return;
    setLoadingAcademicYears(true);
    Promise.all([
      api.get('/api/academic-year/all-active', { params: { tenantId } }),
      api.get('/api/academic-year/all-completed', { params: { tenantId } })
    ])
      .then(([activeRes, completedRes]) => {
        const activeYears = activeRes.data.academicYears || activeRes.data.activeAcademicYears || [];
        const completedYears = completedRes.data.academicYears || [];
        const years = [...activeYears, ...completedYears];
        setAcademicYears(years);
        setAcademicYearsProp?.(years);
      })
      .catch(() => setError('Failed to load academic years'))
      .finally(() => setLoadingAcademicYears(false));
  }, [tenantId, setAcademicYearsProp]);

  // Fetch semesters/terms when academic year changes
  useEffect(() => {
    if (!academicYearId) {
      setSemesters([]);
      setTerms([]);
      setIsSemesterBased(null);
      setSemestersProp?.([]);
      setTermsProp?.([]);
      return;
    }

    const selectedAY = academicYears.find(y => y._id === academicYearId);
    if (selectedAY && isAcademicYearCompleted(selectedAY)) {
      setError('This academic year is completed. View only mode.');
    } else {
      setError('');
    }

    setLoadingSemestersTerms(true);
    setSemesters([]);
    setTerms([]);
    setIsSemesterBased(null);
    
    // First try to fetch semesters
    api.get(`/api/classManager/semesters/${academicYearId}`)
      .then(res => {
        const sems = res.data.semesters || [];
        if (sems.length > 0) {
          setSemesters(sems);
          setSemestersProp?.(sems);
          setIsSemesterBased(true);
        } else {
          // If no semesters, try terms
          api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
            .then(res2 => {
              const trms = res2.data.terms || [];
              setTerms(trms);
              setTermsProp?.(trms);
              setIsSemesterBased(false);
            })
            .catch(() => {
              setError('Failed to load terms');
            });
        }
      })
      .catch(() => {
        // If semesters fetch fails, try terms
        api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
          .then(res2 => {
            const trms = res2.data.terms || [];
            setTerms(trms);
            setTermsProp?.(trms);
            setIsSemesterBased(false);
          })
          .catch(() => {
            setError('Failed to load semesters/terms');
          });
      })
      .finally(() => setLoadingSemestersTerms(false));
  }, [academicYearId, tenantId, academicYears, setSemestersProp, setTermsProp, isAcademicYearCompleted]);

  // Reset selections when dependencies change
  const handleProgramTypeChange = (value) => {
    setProgramTypeId(value);
    setProgramId('');
    setAcademicYearId('');
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
  };

  const handleProgramChange = (value) => {
    setProgramId(value);
    setAcademicYearId('');
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
  };

  const handleAcademicYearChange = (value) => {
    setAcademicYearId(value);
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
  };

  const handleSemesterChange = (value) => {
    setSemesterId(value);

    const selectedSemester = semesters.find(s => s._id === value);
    if (selectedSemester && isSemesterCompleted(selectedSemester)) {
      setError('This semester is completed. View only mode.');
    } else if (!error.startsWith('This academic year is completed')) {
      setError('');
    }
  };

  const handleTermChange = (value) => {
    setTermId(value);

    const selectedTerm = terms.find(t => t._id === value);
    if (selectedTerm && isTermCompleted(selectedTerm)) {
      setError('This term is completed. View only mode.');
    } else if (!error.startsWith('This academic year is completed')) {
      setError('');
    }
  };

  // Notify parent of context changes

  useEffect(() => {
    // Only trigger when both academicYearId and (semesterId or termId) are selected
    if (onContextChange) {
      if (
        programTypeId &&
        programId &&
        academicYearId &&
        ((isSemesterBased === true && semesterId) || (isSemesterBased === false && termId))
      ) {
        const selectedProgramType = programTypes.find(pt => pt._id === programTypeId);
        const selectedProgram = programs.find(p => p._id === programId);
        const selectedAcademicYear = academicYears.find(y => y._id === academicYearId);
        const selectedSemester = semesters.find(s => s._id === semesterId);
        const selectedTerm = terms.find(t => t._id === termId);
        onContextChange({
          programTypeId,
          programTypeName: selectedProgramType?.name || '',
          programId,
          programName: selectedProgram?.name || '',
          academicYearId,
          academicYearName: selectedAcademicYear?.name || selectedAcademicYear?.academicYear || '',
          semId: isSemesterBased === true ? semesterId : null,
          semName: isSemesterBased === true ? (selectedSemester?.name || selectedSemester?.semesterLabel || `Semester ${selectedSemester?.semesterNumber}` || '') : '',
          termId: isSemesterBased === false ? termId : null,
          termName: isSemesterBased === false ? (selectedTerm?.name || selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}` || '') : '',
          evaluationMode: isSemesterBased === true ? 'sem' : 'term',
          isSemesterBased
        });
      }
    }
  }, [programTypeId, programId, academicYearId, semesterId, termId, isSemesterBased, onContextChange, programTypes, programs, academicYears, semesters, terms]);

  // Get display labels
  const getAcademicYearLabel = (ayId) => {
    const ay = academicYears.find(y => y._id === ayId);
    if (!ay) return '';
    const isCompleted = isAcademicYearCompleted(ay);
    return `${ay.name || ay.academicYear}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  const getSemesterLabel = (semId) => {
    const sem = semesters.find(s => s._id === semId);
    if (!sem) return '';
    const isCompleted = isSemesterCompleted(sem);
    return `${sem.name || sem.semesterLabel || `Semester ${sem.semesterNumber}`}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  const getTermLabel = (tId) => {
    const term = terms.find(t => t._id === tId);
    if (!term) return '';
    const isCompleted = isTermCompleted(term);
    return `${term.name || term.termLabel || `Term ${term.termNumber}`}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  // Check if any section is loading
  const isAnyLoading = loadingProgramTypes || loadingPrograms || loadingAcademicYears || loadingSemestersTerms;

  // Show professional loading screen for any loading state
  if (isAnyLoading) {
    return <AcademicContextLoading />;
  }

  return (
    <div className="w-full rounded-2xl border border-blue-100 bg-white shadow-sm mb-5 overflow-hidden transition-all duration-300">
      {/* Header */}
      <div 
        className="cursor-pointer bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 transition-all duration-200 hover:from-blue-700 hover:to-indigo-700"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-white/15 backdrop-blur-sm">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422A12.083 12.083 0 0112 21.5c-4.418 0-8-1.79-8-4V9" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base md:text-lg font-semibold text-white">Academic Context Selection</h2>
              <p className="truncate text-xs md:text-sm text-blue-100">Select program, academic year, and semester or term</p>
            </div>
          </div>
          <div className="text-white transform transition-transform duration-200">
            <svg 
              className={`w-5 h-5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 md:p-5">
          {/* Error Message */}
          {error && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2">
              <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Selection Grid */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            {/* Program Type */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <span className="grid h-5 w-5 place-items-center rounded-md bg-blue-100 text-[10px] font-bold text-blue-600">1</span>
                <span>Program Type</span>
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={programTypeId}
                  onChange={(e) => handleProgramTypeChange(e.target.value)}
                  disabled={loadingProgramTypes}
                  className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm transition-all duration-200 cursor-pointer hover:border-blue-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-wait disabled:bg-slate-100"
                >
                  <option value="">Select Program Type</option>
                  {programTypes.map(pt => (
                    <option key={pt._id} value={pt._id}>{pt.name}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                  {loadingProgramTypes ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  ) : (
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </div>
            </div>

            {/* Program */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <span className="grid h-5 w-5 place-items-center rounded-md bg-blue-100 text-[10px] font-bold text-blue-600">2</span>
                <span>Program</span>
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={programId}
                  onChange={(e) => handleProgramChange(e.target.value)}
                  disabled={!programTypeId || loadingPrograms}
                  className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm transition-all duration-200 cursor-pointer hover:border-blue-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                >
                  <option value="">Select Program</option>
                  {programs.map(p => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                  {loadingPrograms ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  ) : (
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </div>
            </div>

            {/* Academic Year */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <span className="grid h-5 w-5 place-items-center rounded-md bg-blue-100 text-[10px] font-bold text-blue-600">3</span>
                <span>Academic Year</span>
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={academicYearId}
                  onChange={(e) => handleAcademicYearChange(e.target.value)}
                  disabled={!programId || loadingAcademicYears}
                  className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm transition-all duration-200 cursor-pointer hover:border-blue-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                >
                  <option value="">Select Academic Year</option>
                  {academicYears.map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {getAcademicYearLabel(ay._id)}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                  {loadingAcademicYears ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  ) : (
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </div>
            </div>

            {/* Semester or Term based on academic year type */}
            {isSemesterBased === true && (
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <span className="grid h-5 w-5 place-items-center rounded-md bg-green-100 text-[10px] font-bold text-green-600">4</span>
                  <span>Semester</span>
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={semesterId}
                    onChange={(e) => handleSemesterChange(e.target.value)}
                    disabled={!academicYearId || semesters.length === 0 || loadingSemestersTerms}
                    className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm transition-all duration-200 cursor-pointer hover:border-blue-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                  >
                    <option value="">Select Semester</option>
                    {semesters.map(sem => (
                      <option key={sem._id} value={sem._id}>
                        {getSemesterLabel(sem._id)}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    {loadingSemestersTerms ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    ) : (
                      <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                {loadingSemestersTerms && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    Loading semesters...
                  </p>
                )}
                {semesters.length === 0 && academicYearId && !loadingSemestersTerms && (
                  <p className="text-xs text-amber-600 mt-1">No semesters found for this academic year</p>
                )}
              </div>
            )}

            {isSemesterBased === false && (
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <span className="grid h-5 w-5 place-items-center rounded-md bg-green-100 text-[10px] font-bold text-green-600">4</span>
                  <span>Term</span>
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={termId}
                    onChange={(e) => handleTermChange(e.target.value)}
                    disabled={!academicYearId || terms.length === 0 || loadingSemestersTerms}
                    className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm transition-all duration-200 cursor-pointer hover:border-blue-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                  >
                    <option value="">Select Term</option>
                    {terms.map(term => (
                      <option key={term._id} value={term._id}>
                        {getTermLabel(term._id)}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    {loadingSemestersTerms ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    ) : (
                      <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                {loadingSemestersTerms && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    Loading terms...
                  </p>
                )}
                {terms.length === 0 && academicYearId && !loadingSemestersTerms && (
                  <p className="text-xs text-amber-600 mt-1">No terms found for this academic year</p>
                )}
              </div>
            )}
          </div>

          {/* Context Summary */}
          {programTypeId && programId && academicYearId && ((isSemesterBased === true && semesterId) || (isSemesterBased === false && termId)) && (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Selected Context Summary</span>
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                  <div>
                    <span className="text-xs text-slate-500">Program Type</span>
                    <p className="truncate font-medium text-slate-800">{programTypes.find(pt => pt._id === programTypeId)?.name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Program</span>
                    <p className="truncate font-medium text-slate-800">{programs.find(p => p._id === programId)?.name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Academic Year</span>
                    <p className="truncate font-medium text-slate-800">{getAcademicYearLabel(academicYearId)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">{isSemesterBased ? 'Semester' : 'Term'}</span>
                    <p className="truncate font-medium text-slate-800">
                      {isSemesterBased 
                        ? getSemesterLabel(semesterId)
                        : getTermLabel(termId)
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AcademicContextSelector;